import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { parseISO } from 'date-fns'
import { toast } from 'sonner'
import { ApiError, http } from './api'
import { clientsApi } from './clients-api'
import { errorMessage } from './errors'
import { addDecimals, toNumber, type Decimal } from './decimal'
import { formatHmm } from './hours'
import { timeApi } from './time-api'
import { rowLabel } from './timesheet-copy'
import type { UtilizationParams } from './time-api/adapter'
import type { IsoDate, Me, SetAbsenceInput, WeekResponse } from './types'
import { formatDayLabel, isoWeekId } from './week'

export const timeKeys = {
  me: ['time', 'me'] as const,
  pickers: (personId?: string) => ['time', 'pickers', personId ?? 'me'] as const,
  week: (weekId: string, personId?: string) => ['time', 'week', weekId, personId ?? 'me'] as const,
  weekTotals: (weekId: string, personId?: string) => ['time', 'weekTotals', weekId, personId ?? 'me'] as const,
  weekHistory: (weekId: string, personId?: string) => ['time', 'weekHistory', weekId, personId ?? 'me'] as const,
  team: (weekId: string) => ['time', 'team', weekId] as const,
  teamDay: (date: string) => ['time', 'teamDay', date] as const,
  teamTotals: (weekId: string) => ['time', 'teamTotals', weekId] as const,
  utilization: (params: UtilizationParams) =>
    ['time', 'utilization', params.personId ?? 'me', params.period ?? 'week', params.anchor ?? 'now'] as const,
  exceptions: (from: string, to: string, personId?: string) =>
    ['time', 'exceptions', personId ?? 'me', from, to] as const,
}

export interface CellValues {
  hours: Decimal
  description: string
  travelAmount: Decimal | null
}

export interface SaveCellVars {
  rowKey: string
  dayIndex: number
  date: string
  projectId: string
  categoryId: string | null
  entryId: string | null
  values: CellValues
}

function patchWeek(week: WeekResponse, vars: SaveCellVars): WeekResponse {
  const rows = week.rows.map((row) => {
    if (row.rowKey !== vars.rowKey) return row
    const cells = row.cells.map((cell) =>
      cell.dayIndex === vars.dayIndex
        ? {
            ...cell,
            hours: vars.values.hours,
            description: vars.values.description,
            travelAmount: vars.values.travelAmount,
          }
        : cell,
    )
    return { ...row, cells, rowTotal: addDecimals(cells.map((c) => c.hours)) }
  })
  const dailyTotals = week.dailyTotals.map((day) => ({
    ...day,
    total: addDecimals(rows.map((r) => r.cells[day.dayIndex].hours)),
  }))
  return { ...week, rows, dailyTotals, weekTotal: addDecimals(dailyTotals.map((d) => d.total)) }
}

export function useMe() {
  return useQuery({ queryKey: timeKeys.me, queryFn: () => timeApi.me() })
}

export function usePickers(personId?: string) {
  return useQuery({
    queryKey: timeKeys.pickers(personId),
    queryFn: () => timeApi.pickers(personId),
    // personId start ongedefinieerd (sleutel 'me') tot /api/me is opgehaald en verschuift
    // dan naar de echte id: een andere querysleutel, dus zonder placeholder valt de UI
    // terug naar een laadstaat en verdwijnt bijvoorbeeld een net geopende toevoegzone.
    placeholderData: (previous) => previous,
  })
}

export function useWeek(weekId: string, personId?: string, enabled = true) {
  return useQuery({
    queryKey: timeKeys.week(weekId, personId),
    queryFn: () => timeApi.week(weekId, personId),
    // Zelfde reden als usePickers: de sleutel verschuift zodra personId bekend wordt, en
    // zonder placeholder klapt de lege-staat kortstondig terug naar de laadstaat.
    placeholderData: (previous) => previous,
    enabled,
  })
}

export function useWeekTotals(weekId: string, personId?: string, enabled = true) {
  return useQuery({
    queryKey: timeKeys.weekTotals(weekId, personId),
    queryFn: () => timeApi.weekTotals(weekId, personId),
    placeholderData: (previous) => previous,
    enabled,
  })
}

export function useWeekHistory(weekId: string, personId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: timeKeys.weekHistory(weekId, personId),
    queryFn: () => timeApi.weekHistory(weekId, personId),
    enabled,
  })
}

/**
 * Namen los van een week. Het teamoverzicht laat iemand zonder verplichte uren weg
 * in een week waarin hij niets boekte, dus een gedeelde link naar zo iemand zou
 * anders een naamloze pagina opleveren.
 */
export function useTeamPersonNames(enabled = true) {
  return useQuery({
    queryKey: ['time', 'personNames'] as const,
    queryFn: () => http.get<{ persons: Array<{ id: string; displayName: string }> }>('/api/persons'),
    select: (data) => new Map(data.persons.map((person) => [person.id, person.displayName])),
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

export function useTeamWeek(weekId: string, enabled = true) {
  return useQuery({ queryKey: timeKeys.team(weekId), queryFn: () => timeApi.teamWeek(weekId), enabled })
}

export function useTeamDay(date: string) {
  // Bewust geen placeholderData, net als useTeamWeek: die houdt bij een dagstap de
  // cijfers van de vorige dag in beeld terwijl de kop de nieuwe dag al noemt, en
  // `isLoading` staat dan op false. Een skelet is eerlijker dan een verkeerd getal.
  return useQuery({ queryKey: timeKeys.teamDay(date), queryFn: () => timeApi.teamDay(date) })
}

export function useTeamWeekTotals(weekId: string, enabled = true) {
  return useQuery({
    queryKey: timeKeys.teamTotals(weekId),
    queryFn: () => timeApi.teamWeekTotals(weekId),
    placeholderData: (previous) => previous,
    enabled,
  })
}

export function useSaveCell(personId: string | undefined, me: Me | undefined) {
  const queryClient = useQueryClient()
  // De sleutel volgt de werkdatum van de regel, niet de week die op dat moment in beeld is:
  // wie meteen na het opslaan wegnavigeert, zou anders de week invalideren waar hij naartoe
  // ging in plaats van de week waarin hij boekte. De uren staan dan wel op de server maar
  // blijven onzichtbaar tot de cache alsnog verloopt.
  const keyFor = (date: string) => timeKeys.week(isoWeekId(parseISO(date)), personId)

  return useMutation({
    mutationFn: async (vars: SaveCellVars) => {
      const target = toNumber(vars.values.hours)
      if (vars.entryId) {
        if (target <= 0) {
          await timeApi.deleteEntry(vars.entryId)
          return
        }
        await timeApi.updateEntry(vars.entryId, {
          hours: vars.values.hours,
          description: vars.values.description,
          travelAmount: vars.values.travelAmount,
          categoryId: vars.categoryId,
        })
        return
      }
      if (target <= 0) return
      const ownerId = personId ?? me?.person?.id
      if (!ownerId) throw new ApiError(409, { error: { code: 'conflict', message: 'Geen persoon gekoppeld' } })
      await timeApi.createEntry({
        personId: ownerId,
        projectId: vars.projectId,
        categoryId: vars.categoryId,
        workDate: vars.date,
        hours: vars.values.hours,
        description: vars.values.description,
        travelAmount: vars.values.travelAmount,
      })
    },
    onMutate: async (vars) => {
      const key = keyFor(vars.date)
      const previous = queryClient.getQueryData<WeekResponse>(key)
      // Wat er verdwijnt, nu vastleggen: na afloop staat de regel niet meer in de cache
      // en valt er niets meer af te lezen voor de melding.
      const row = previous?.rows.find((r) => r.rowKey === vars.rowKey)
      const gone = row?.cells.find((c) => c.dayIndex === vars.dayIndex)
      const removed =
        row && gone && vars.entryId !== null && toNumber(vars.values.hours) <= 0 && toNumber(gone.hours) > 0
          ? `${formatHmm(toNumber(gone.hours))} op ${rowLabel(row.clientName, row.projectName, row.categoryName)}`
          : null
      // De patch gaat voor op het annuleren: zou hij achter een await staan, dan rendert
      // de dagweergave eerst een frame met de nieuwe sleutel en de oude cel, en knippert
      // de regel alsnog.
      if (previous?.rows.some((r) => r.rowKey === vars.rowKey)) {
        queryClient.setQueryData<WeekResponse>(key, patchWeek(previous, vars))
      }
      await queryClient.cancelQueries({ queryKey: key })
      return { previous, removed }
    },
    // Een cel leegmaken verwijdert de entry, en dat gebeurde tot nu toe geluidloos: in de
    // dagtabel bij het verlaten van de rij, in de celpopover via de prullenbak.
    onSuccess: (_data, _vars, context) => {
      if (context?.removed) toast.success(`${context.removed} verwijderd`)
    },
    onError: (error, vars, context) => {
      if (context?.previous) queryClient.setQueryData(keyFor(vars.date), context.previous)
      toast.error(errorMessage(error, 'Opslaan mislukt'))
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({ queryKey: keyFor(vars.date) })
      queryClient.invalidateQueries({ queryKey: timeKeys.team(isoWeekId(parseISO(vars.date))) })
      queryClient.invalidateQueries({ queryKey: ['time', 'weekTotals'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'teamTotals'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'teamDay'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'weekHistory'] })
    },
  })
}

/** Invalidatie na een schrijfactie op een hele regel (verwijderen, categorie- of projectwissel). */
function invalidateRowWrite(queryClient: QueryClient, weekId: string, personId?: string) {
  queryClient.invalidateQueries({ queryKey: timeKeys.week(weekId, personId) })
  queryClient.invalidateQueries({ queryKey: timeKeys.team(weekId) })
  queryClient.invalidateQueries({ queryKey: ['time', 'weekTotals'] })
  queryClient.invalidateQueries({ queryKey: ['time', 'teamTotals'] })
  queryClient.invalidateQueries({ queryKey: ['time', 'teamDay'] })
  queryClient.invalidateQueries({ queryKey: ['time', 'weekHistory'] })
}

/**
 * Verwijdert de entries die de aanroeper meegeeft: de hele regel in de weekweergave, of
 * alleen de dag die in beeld staat in de dagweergave. Dat zijn hooguit zeven entries, dus
 * een lus over het bestaande delete-endpoint volstaat: geen extra endpoint en dus ook geen
 * extra MCP-tegenhanger.
 */
export function useDeleteRow(weekId: string, personId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ entryIds }: { entryIds: string[]; label: string }) => {
      for (const id of entryIds) {
        await timeApi.deleteEntry(id)
      }
    },
    onSuccess: (_result, { label }) => {
      invalidateRowWrite(queryClient, weekId, personId)
      toast.success(`${label} verwijderd`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}

export function useCreatePickerCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, projectId, name }: { clientId: string; projectId: string; name: string }) =>
      clientsApi.createCategory(clientId, { name, projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'pickers'] })
    },
    onError: (e) => toast.error(errorMessage(e, 'Categorie aanmaken mislukt')),
  })
}

export function useChangeRowCategory(weekId: string, personId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ entryIds, categoryId }: { entryIds: string[]; categoryId: string | null }) => {
      for (const id of entryIds) {
        await timeApi.updateEntry(id, { categoryId })
      }
    },
    onSuccess: () => {
      invalidateRowWrite(queryClient, weekId, personId)
      toast.success('Categorie gewijzigd')
    },
    onError: (e) => toast.error(errorMessage(e, 'Categorie wijzigen mislukt')),
  })
}

/**
 * Verplaatst een hele regel (alle entries in de getoonde week) naar een ander project
 * van dezelfde klant. Project en categorie gaan in een PATCH: los verstuurd zou de
 * oude categorie op het nieuwe project een 422 geven. Geen optimistic update: de
 * rowKey verandert, dus de rij verspringt hoe dan ook na de refetch.
 */
export function useChangeRowProject(weekId: string, personId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      entryIds,
      projectId,
      categoryId,
    }: {
      entryIds: string[]
      projectId: string
      categoryId: string | null
      label: string
    }) => {
      for (const id of entryIds) {
        await timeApi.updateEntry(id, { projectId, categoryId })
      }
    },
    // Een halverwege gestrande lus laat de regel over twee projecten verdeeld achter;
    // juist dan moet het scherm de waarheid ophalen in plaats van de oude staat houden.
    onSettled: () => invalidateRowWrite(queryClient, weekId, personId),
    onSuccess: (_result, { entryIds, label }) => {
      toast.success(entryIds.length > 1 ? `${entryIds.length} regels verplaatst naar ${label}` : `Verplaatst naar ${label}`)
    },
    onError: (e) => toast.error(errorMessage(e, 'Project wijzigen mislukt')),
  })
}

/** `through` sluit alleen het deel van de week t/m die dag af (een maandeinde). */
export function useApproveWeek(weekId: string, personId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (through?: IsoDate) => timeApi.approveWeek(weekId, personId, through),
    onSuccess: (approval) => {
      queryClient.invalidateQueries({ queryKey: timeKeys.week(weekId, personId) })
      queryClient.invalidateQueries({ queryKey: timeKeys.team(weekId) })
      queryClient.invalidateQueries({ queryKey: ['time', 'teamDay'] })
      toast.success(
        approval.approvedThrough ? `Afgesloten t/m ${formatDayLabel(approval.approvedThrough)}` : 'Week afgesloten',
      )
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Afsluiten mislukt')
    },
  })
}

export function useReopenWeek(weekId: string, personId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => timeApi.reopenWeek(weekId, personId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeKeys.week(weekId, personId) })
      queryClient.invalidateQueries({ queryKey: timeKeys.team(weekId) })
      queryClient.invalidateQueries({ queryKey: ['time', 'teamDay'] })
      toast.success('Week heropend')
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Heropenen mislukt')
    },
  })
}

export function useApproveWeekAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { weekId: string; personId: string; through?: IsoDate }) =>
      timeApi.approveWeek(vars.weekId, vars.personId, vars.through),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time'] })
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Afsluiten mislukt')
    },
  })
}

export function useUtilizationReport(params: UtilizationParams, enabled = true) {
  return useQuery({
    queryKey: timeKeys.utilization(params),
    queryFn: () => timeApi.utilizationReport(params),
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useWorkdayExceptions(from: string, to: string, personId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: timeKeys.exceptions(from, to, personId),
    queryFn: () => timeApi.workdayExceptions(from, to, personId),
    enabled,
  })
}

function invalidateAbsence(queryClient: ReturnType<typeof useQueryClient>, weekId: string) {
  queryClient.invalidateQueries({ queryKey: timeKeys.week(weekId) })
  queryClient.invalidateQueries({ queryKey: ['time', 'week'] })
  queryClient.invalidateQueries({ queryKey: timeKeys.team(weekId) })
  queryClient.invalidateQueries({ queryKey: ['time', 'exceptions'] })
  queryClient.invalidateQueries({ queryKey: ['time', 'utilization'] })
}

export function useSetAbsence(weekId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SetAbsenceInput) => timeApi.setAbsence(input),
    onSuccess: (created) => {
      invalidateAbsence(queryClient, weekId)
      toast.success(created.length > 1 ? `${created.length} dagen gemarkeerd` : 'Dag gemarkeerd')
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Markeren mislukt')
    },
  })
}

export function useDeleteAbsences(weekId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await timeApi.deleteAbsence(id)
      }
    },
    onSuccess: (_result, ids) => {
      invalidateAbsence(queryClient, weekId)
      toast.success(ids.length > 1 ? `${ids.length} vakantiedagen verwijderd` : 'Markering verwijderd')
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Verwijderen mislukt')
    },
  })
}
